import type { AppointmentDetail, CatalogItem, AppointmentStatus, AppointmentView, CalendarResponse, EditAppointmentInput } from "@vaulted/shared";
import { HoldBadge } from "./HoldBadge";
import { STATUS_LABELS, formatCents, formatPhone } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { ApiError, errorMessage, get, post } from "../api";
import { type CatalogResponse } from "../pages/Catalog";
import { displayName } from "../pages/Clients";
import { useSession } from "../session";
import { ServicesPicker } from "./ServicesPicker";
import { Alert, Badge, Button, Checkbox, Dialog, Field, Loading, SelectField, formatDateTime } from "./ui";

/** "Forms ✓" or "Forms needed (2)", for cards and lists. */
const DEPOSIT_LABEL: Record<AppointmentDetail["deposit"]["status"], string> = {
  none: "none",
  due: "not paid yet",
  held: "held on card",
  paid: "paid",
  released: "released",
  forfeited: "kept (late cancel)",
  refunded: "refunded",
};

export function FormsBadge({ a, compact }: { a: Pick<AppointmentView, "formsNeeded" | "status"> & { finishedInBoulevard?: boolean }; compact?: boolean }) {
  if (a.status === "cancelled" || a.status === "no_show") return null;
  // Signed in Boulevard (they're under the client's Forms, as imported).
  if (a.finishedInBoulevard) return <span className="text-xs font-semibold text-muted">{compact ? "Forms in Boulevard" : "Forms signed in Boulevard"}</span>;
  const n = a.formsNeeded.length;
  return n ? (
    <span className="text-xs font-semibold text-wait" title={a.formsNeeded.map((f) => f.name).join(", ")}>
      {compact ? `Forms (${n})` : `Forms needed (${n})`}
    </span>
  ) : (
    <span className="text-xs font-semibold text-ok">Forms ✓</span>
  );
}

export const STATUS_TONE: Record<AppointmentStatus, "ok" | "wait" | "off" | "brand"> = {
  unconfirmed: "wait",
  confirmed: "brand",
  arrived: "ok",
  active: "ok",
  completed: "off",
  cancelled: "off",
  no_show: "off",
};

/** The main next step for each status (front desk). Once they're here, it's the visit screen. */
export const NEXT_ACTION: Partial<Record<AppointmentStatus, { to: AppointmentStatus; label: string; visit?: boolean }>> = {
  unconfirmed: { to: "confirmed", label: "Confirm" },
  confirmed: { to: "arrived", label: "Check in" },
  arrived: { to: "active", label: "Open visit", visit: true },
  active: { to: "completed", label: "Open visit", visit: true },
};

/** Each service and add-on with its price, when there's more than one line (one line is just the price). */
function ChargeLines({ a }: { a: AppointmentView }) {
  const lines = a.services.length
    ? a.services.flatMap((s) => [{ name: s.name, cents: s.priceCents, addOn: false }, ...s.addOns.map((x) => ({ name: x.name, cents: x.priceCents, addOn: true }))])
    : [{ name: a.serviceName, cents: a.priceCents - a.addOns.reduce((t, x) => t + x.priceCents, 0), addOn: false }, ...a.addOns.map((x) => ({ name: x.name, cents: x.priceCents, addOn: true }))];
  if (lines.length < 2) return null;
  return (
    <dd className="mt-1 flex flex-col gap-0.5 text-xs text-ink-soft">
      {lines.map((l, i) => (
        <span key={i} className="flex justify-between gap-3">
          <span className={l.addOn ? "pl-3" : ""}>{l.name}</span>
          <span className="tabular-nums">{formatCents(l.cents)}</span>
        </span>
      ))}
    </dd>
  );
}

export const time12 = (local: string) => {
  const [h, m] = local.slice(11, 16).split(":").map(Number) as [number, number];
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
};

/** Refresh a visit everywhere it shows (the visit page, the calendar, the front desk). */
function useVisitSaved(id: string) {
  const qc = useQueryClient();
  return async (d: AppointmentDetail) => {
    qc.setQueryData(["appointment", id], d);
    await qc.invalidateQueries({ queryKey: ["calendar"] });
  };
}

/** Things to check before the visit: Boulevard's hold on it, the guardian link, a booking against the rules. */
export function VisitAlerts({ a }: { a: AppointmentDetail }) {
  const saved = useVisitSaved(a.id);
  const guardian = useMutation({ mutationFn: (confirm: boolean) => post<AppointmentDetail>(`/api/appointments/${a.id}/guardian-check`, { confirm }), onSuccess: saved });
  return (
    <>
      {a.managedInBoulevard && <p className="text-sm text-muted">Managed in Boulevard until it starts: change, cancel, or rebook it there, and the next sync brings it over.</p>}
      {a.boulevardFlags.length > 0 && (
        <div role="status" className="rounded-[var(--radius-control)] bg-wait-wash px-3 py-2 text-sm text-wait">
          <span className="font-semibold">Check:</span> {a.boulevardFlags.map((f) => f.message).join("; ")} (Settings → Boulevard).
        </div>
      )}
      {a.guardianCheck ? (
        <div role="status" className="flex flex-col gap-2 rounded-[var(--radius-control)] bg-wait-wash px-3 py-2.5 text-sm text-wait">
          <p>
            <span className="font-semibold">Check guardian:</span> {a.guardianCheck.name} booked this online and says they're {a.client.firstName}'s parent or legal guardian, but isn't linked as guardian on {a.client.firstName}'s record.
          </p>
          {!!guardian.error && <Alert>{errorMessage(guardian.error)}</Alert>}
          <div className="flex flex-wrap gap-2">
            <Button busy={guardian.isPending && guardian.variables} onClick={() => guardian.mutate(true)}>
              Link {a.guardianCheck.name} as guardian
            </Button>
            <Button variant="quiet" busy={guardian.isPending && !guardian.variables} onClick={() => guardian.mutate(false)}>
              They're not the guardian
            </Button>
          </div>
        </div>
      ) : (
        a.client.minorWithoutGuardian && <Alert tone="info">Minor with no guardian linked on their record.</Alert>
      )}
      {a.servicesOverrideReason && <Alert tone="info">Booked against the visit rules: {a.servicesOverrideReason}</Alert>}
    </>
  );
}

/** When, where, who, the price, and the booking it's part of. */
export function VisitDetails({ a, onEdit, editLabel = "Edit booking" }: { a: AppointmentDetail; onEdit?: () => void; editLabel?: string }) {
  const { can } = useSession();
  const [changingNurse, setChangingNurse] = useState(false);
  return (
    <section className="flex flex-col gap-3 rounded-[20px] border border-line bg-white p-5">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-lg font-bold">Booking</h2>
        {onEdit && (
          <Button variant="quiet" className="order-last ml-auto" onClick={onEdit}>
            {editLabel}
          </Button>
        )}
        <span className="text-sm text-muted">{a.bookingReference}</span>
        {a.bookingSource === "online" && <Badge tone="brand">Online</Badge>}
        {a.bookingSource === "walk_in" && <Badge tone="brand">Walk-in</Badge>}
        {a.bookingSource === "boulevard" && <Badge tone="brand">Boulevard</Badge>}
      </div>
      <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
        <div>
          <dt className="text-muted">When</dt>
          <dd className="font-semibold">
            {a.start.slice(0, 10)} · {time12(a.start)}–{time12(a.end)}
          </dd>
          {a.context !== "mobile" && <dd className="text-xs text-muted">Nurse busy until {time12(a.staffEnd)}</dd>}
        </div>
        {a.context !== "clinic" && (
          <div>
            <dt className="text-muted">Where</dt>
            <dd className="font-semibold">
              {a.context === "mobile" ? (
                <>
                  {a.address}{" "}
                  <Link to={`/mobile-requests/${a.bookingId}`} className="font-normal text-brand underline">
                    {a.bookingStatus === "requested" ? "Request: waiting for a nurse" : "Mobile request"}
                  </Link>
                </>
              ) : (
                a.eventName
              )}
            </dd>
            {a.context === "mobile" && a.staffFrom !== a.start && <dd className="text-xs text-muted">Nurse travels from {time12(a.staffFrom)} to {time12(a.staffEnd)}</dd>}
          </div>
        )}
        <div>
          <dt className="text-muted">Nurse</dt>
          <dd className="font-semibold">
            {a.staffName ?? "Unassigned"}
            {can("appointments.manage") && !a.managedInBoulevard && a.status !== "cancelled" && (
              <button type="button" className="ml-2 text-sm font-semibold text-brand" onClick={() => setChangingNurse(true)}>
                Change
              </button>
            )}
          </dd>
          {changingNurse && <ChangeNurse a={a} onClose={() => setChangingNurse(false)} />}
        </div>
        <div>
          <dt className="text-muted">{a.boulevardReceipt ? "Paid in Boulevard" : a.finishedInBoulevard ? "Price as booked in Boulevard" : "Price"}</dt>
          <dd className="font-semibold">
            {formatCents(a.boulevardReceipt ? a.boulevardReceipt.totalCents : a.priceCents)}
            {!a.boulevardReceipt && a.discountCents > 0 && <span className="font-normal text-muted"> ({a.bookingSource === "boulevard" ? "discount" : "group discount"} {formatCents(a.discountCents)})</span>}
          </dd>
          {!a.boulevardReceipt && <ChargeLines a={a} />}
          {a.priceOverrideReason && <dd className="text-xs text-wait">Price changed: {a.priceOverrideReason}</dd>}
          {a.coveredBy && <dd className="text-xs font-semibold text-ok">Covered by {a.coveredBy}</dd>}
        </div>
        {a.client.mobile && (
          <div>
            <dt className="text-muted">Phone</dt>
            <dd className="font-semibold">{formatPhone(a.client.mobile)}</dd>
          </div>
        )}
        {a.booker.id !== a.client.id && (
          <div>
            <dt className="text-muted">Booked by</dt>
            <dd className="font-semibold">
              <Link to={`/clients/${a.booker.id}`} className="hover:underline">
                {a.booker.firstName} {a.booker.lastName}
              </Link>
            </dd>
          </div>
        )}
      </dl>
      {a.bookingAppointments.length > 1 && (
        <div className="text-sm">
          <span className="text-muted">Same booking: </span>
          {a.bookingAppointments
            .filter((x) => x.id !== a.id)
            .map((x, i) => (
              <span key={x.id}>
                {i > 0 && "; "}
                <Link to={`/visits/${x.id}`} className="hover:underline">
                  {x.clientName} ({x.serviceName}, {time12(x.start)})
                </Link>
              </span>
            ))}
        </div>
      )}
      {a.notes && <p className="text-sm">Note: {a.notes}</p>}
    </section>
  );
}

/** Card on file, deposit, and fees; and a link for the client to add a card. */
export function VisitMoney({ a }: { a: AppointmentDetail }) {
  const [cardLink, setCardLink] = useState<string | null>(null);
  const sendCard = useMutation({
    mutationFn: () => post<{ link: string; sent: boolean }>(`/api/appointments/${a.id}/card-link`, { send: true }),
    onSuccess: (r) => setCardLink(r.sent ? "Sent to the client." : `Couldn't text it; copy the link: ${r.link}`),
  });
  if (a.finishedInBoulevard) return null;
  return (
    <div className="flex flex-col gap-1.5 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        {a.card ? (
          <span className="text-ok">{a.card.brand === "link" ? "Card on file (saved with Stripe Link)" : `Card on file: ${a.card.brand.toUpperCase()} •••• ${a.card.last4}`}</span>
        ) : a.bookingStatus === "pending" ? (
          a.holdUntil ? <HoldBadge until={a.holdUntil} /> : <span className="text-wait">Waiting for the client to finish adding their card online</span>
        ) : a.context === "clinic" ? (
          <span className="text-wait">No card on file</span>
        ) : null}
        {a.deposit.cents > 0 && (
          <span className="text-ink-soft">
            · Deposit {formatCents(a.deposit.cents)}: {DEPOSIT_LABEL[a.deposit.status]}
          </span>
        )}
      </div>
      {a.payments
        .filter((p) => p.kind === "fee")
        .map((p) => (
          <div key={p.id} className={p.status === "succeeded" ? "text-ink-soft" : "text-danger"}>
            {p.reason} fee {formatCents(p.amountCents)}: {p.status === "succeeded" ? `charged to •••• ${p.cardLast4}` : `not charged (${p.failureMessage})`}
          </div>
        ))}
      {!a.card && a.context === "clinic" && !["completed", "cancelled", "no_show"].includes(a.status) && (
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="quiet" busy={sendCard.isPending} onClick={() => sendCard.mutate()}>
            Text a link to add a card
          </Button>
          {cardLink && <span className="text-xs text-muted">{cardLink}</span>}
        </div>
      )}
      {sendCard.error && <Alert>{errorMessage(sendCard.error)}</Alert>}
    </div>
  );
}

/** Cancel, no-show (each with its fee), or put a cancelled visit back. */
export function VisitEndActions({ a }: { a: AppointmentDetail }) {
  const saved = useVisitSaved(a.id);
  const [ending, setEnding] = useState<"cancelled" | "no_show" | null>(null);
  const [reason, setReason] = useState("");
  const [chargeFee, setChargeFee] = useState(true);
  const status = useMutation({
    mutationFn: (to: AppointmentStatus) => post<AppointmentDetail>(`/api/appointments/${a.id}/status`, to === "confirmed" ? { status: to } : { status: to, reason, chargeFee }),
    onSuccess: async (d) => {
      await saved(d);
      setEnding(null);
      setReason("");
    },
  });
  if (a.managedInBoulevard) return null;
  if (ending) {
    return (
      <form className="flex flex-col gap-2 rounded-[20px] border border-line bg-white p-5" onSubmit={(e) => (e.preventDefault(), status.mutate(ending))}>
        <Field label={ending === "cancelled" ? "Why is it cancelled?" : "No-show note"} required autoFocus value={reason} onChange={(e) => setReason(e.target.value)} />
        {a.coveredBy && a.context === "clinic" && (ending === "no_show" || a.lateCancel) && <p className="text-sm text-ink-soft">This uses the {a.coveredBy} voucher held for the visit, instead of a fee.</p>}
        {a.feeIfLate != null && !a.coveredBy && (ending === "no_show" || a.lateCancel) && (
          <Checkbox
            label={`Charge the ${formatCents(a.feeIfLate)} ${ending === "no_show" ? "no-show" : "late-cancellation"} fee${a.card ? ` to •••• ${a.card.last4}` : " (no card on file: it will be recorded as unpaid)"}`}
            description="Untick if we cancelled, or to waive it."
            checked={chargeFee}
            onChange={setChargeFee}
          />
        )}
        {status.error && <Alert>{errorMessage(status.error)}</Alert>}
        <div className="flex gap-2">
          <Button variant="danger" type="submit" busy={status.isPending}>
            {ending === "cancelled" ? "Cancel appointment" : "Mark no-show"}
          </Button>
          <Button type="button" variant="quiet" onClick={() => setEnding(null)}>
            Back
          </Button>
        </div>
      </form>
    );
  }
  const can = { cancel: ["unconfirmed", "confirmed", "arrived"].includes(a.status), noShow: ["unconfirmed", "confirmed"].includes(a.status), reinstate: a.status === "cancelled" || a.status === "no_show" };
  if (!can.cancel && !can.noShow && !can.reinstate) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {can.reinstate && (
        <Button busy={status.isPending} onClick={() => status.mutate("confirmed")}>
          Reinstate
        </Button>
      )}
      {can.cancel && (
        <Button variant="quiet" onClick={() => setEnding("cancelled")}>
          Cancel appointment…
        </Button>
      )}
      {can.noShow && (
        <Button variant="quiet" onClick={() => setEnding("no_show")}>
          No-show…
        </Button>
      )}
      {status.error && <Alert>{errorMessage(status.error)}</Alert>}
    </div>
  );
}

/** Every status change: when, who, and why. */
export function VisitHistory({ a }: { a: AppointmentDetail }) {
  return (
    <details className="text-sm">
      <summary className="cursor-pointer font-semibold text-muted">History</summary>
      <ul className="mt-1">
        {a.history.map((h, i) => (
          <li key={i}>
            {formatDateTime(h.at)}: {h.from ? `${STATUS_LABELS[h.from as AppointmentStatus]} → ` : ""}
            {STATUS_LABELS[h.to as AppointmentStatus]}
            {h.by && ` by ${h.by}`}
            {h.reason && ` (${h.reason})`}
          </li>
        ))}
      </ul>
    </details>
  );
}

/**
 * Changing a booking. `when`: just the time, nurse, and price (services change on the visit's
 * Treatment card once there's a chart).
 */
/** Who did the visit, at any point (even after checkout): fixing a mistake, with a reason. Nothing else moves. */
function ChangeNurse({ a, onClose }: { a: AppointmentDetail; onClose: () => void }) {
  const qc = useQueryClient();
  const day = a.start.slice(0, 10);
  const cal = useQuery({ queryKey: ["calendar", day, day], queryFn: () => get<CalendarResponse>(`/api/calendar?from=${day}&to=${day}`) });
  const [staffId, setStaffId] = useState(a.staffId ?? "");
  const [reason, setReason] = useState("");
  const save = useMutation({
    mutationFn: () => post(`/api/appointments/${a.id}/nurse`, { staffId, reason }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["appointment", a.id] });
      await qc.invalidateQueries();
      onClose();
    },
  });
  return (
    <Dialog
      title="Change nurse"
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={save.isPending} disabled={!staffId || staffId === a.staffId || !reason.trim()} onClick={() => save.mutate()}>
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <SelectField label="Nurse" value={staffId} onChange={(e) => setStaffId(e.target.value)}>
          <option value="">Pick a nurse…</option>
          {(cal.data?.staff ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </SelectField>
        <Field label="Reason" required maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Sarah did this visit, not Annalissa" />
        <p className="text-sm text-muted">Only the nurse changes (not the time), and their schedule isn't checked. Pay and commission go to the new nurse; a signed chart keeps who signed it.</p>
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
      </div>
    </Dialog>
  );
}

export function EditAppointment({ a, onClose, when = false }: { a: AppointmentDetail; onClose: () => void; when?: boolean }) {
  const qc = useQueryClient();
  const [v, setV] = useState<EditAppointmentInput>({
    services: a.services.map((x) => ({ serviceId: x.serviceId, addOnIds: x.addOns.map((y) => y.id) })),
    staffId: a.staffId,
    start: a.start,
    // Prices change at checkout (Discount). A time or nurse change keeps an older set price; changing
    // the services goes back to the menu prices.
    priceCents: when && a.priceOverrideReason ? a.priceCents : null,
    priceReason: "",
    servicesReason: "",
    forceReason: null,
    reason: "",
    offMenuReason: "",
  });
  const [conflict, setConflict] = useState<string | null>(null);
  const [offMenu, setOffMenu] = useState<string | null>(null);
  const catalog = useQuery({ queryKey: ["catalog"], queryFn: () => get<CatalogResponse>("/api/catalog") });
  const date = v.start.slice(0, 10);
  const cal = useQuery({ queryKey: ["calendar", date, date], queryFn: () => get<CalendarResponse>(`/api/calendar?from=${date}&to=${date}`) });
  const save = useMutation({
    mutationFn: () => post<AppointmentDetail>(`/api/appointments/${a.id}/edit`, v),
    onSuccess: async (d) => {
      qc.setQueryData(["appointment", a.id], d);
      await qc.invalidateQueries({ queryKey: ["calendar"] });
      // The chart and the bill follow a change of service or add-ons.
      await qc.invalidateQueries({ queryKey: ["chart"] });
      await qc.invalidateQueries({ queryKey: ["checkout", a.id] });
      onClose();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409 && a.context === "clinic") setConflict(e.message);
      // Event visits: something not on the event's menu needs a reason.
      if (e instanceof ApiError && e.code === "off_menu") setOffMenu(e.message);
    },
  });
  const items = catalog.data?.items ?? [];
  const booked = a.services.map((x) => x.serviceId);
  const services = items.filter((i) => i.type === "service" && (i.status === "active" || booked.includes(i.id)) && (a.context !== "clinic" || i.availableClinic) && (a.context !== "mobile" || i.availableMobile));
  const priceHere = (i: CatalogItem) => (a.context === "mobile" ? (i.mobilePriceCents ?? i.priceCents) : i.priceCents);
  const fixedPlace = a.context !== "clinic";
  const set = (patch: Partial<EditAppointmentInput>) => {
    setV((cur) => ({ ...cur, ...patch }));
    setConflict(null);
  };
  return (
    <Dialog
      title={`${when ? "Time and nurse" : "Edit booking"}: ${displayName(a.client)}`}
      onClose={onClose}
      wide
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Back
          </Button>
          <Button variant="primary" busy={save.isPending} disabled={!!conflict && !v.forceReason} onClick={() => save.mutate()}>
            {conflict ? "Save anyway" : "Save changes"}
          </Button>
        </>
      }
    >
      {!catalog.data ? (
        <Loading />
      ) : (
        <div className="flex flex-col gap-4">
          {!when && <ServicesPicker items={items} services={services} value={v.services} onChange={(list) => set({ services: list })} price={priceHere} keep={a.addOns.map((x) => x.id)} reason={v.servicesReason} onReason={(r) => set({ servicesReason: r })} />}
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Date" type="date" disabled={fixedPlace} value={date} onChange={(e) => e.target.value && set({ start: `${e.target.value}T${v.start.slice(11, 16)}` })} />
            <Field label="Time" type="time" step={300} disabled={fixedPlace} value={v.start.slice(11, 16)} onChange={(e) => e.target.value && set({ start: `${date}T${e.target.value}` })} />
            <SelectField label="Nurse" disabled={a.context === "mobile"} value={v.staffId ?? ""} onChange={(e) => set({ staffId: e.target.value || null })}>
              {(cal.data?.staff ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
              {v.staffId && !cal.data?.staff.some((s) => s.id === v.staffId) && <option value={v.staffId}>Current nurse</option>}
            </SelectField>
          </div>
          {fixedPlace && <p className="text-sm text-muted">{a.context === "mobile" ? "Mobile visits keep their time and nurse here; the visit's length follows the service and add-ons." : "Event visits keep their time; the nurse can change."}</p>}
          {offMenu && (
            <div className="flex flex-col gap-2">
              <Alert tone="info">{offMenu}</Alert>
              <Field label="Reason to add it anyway (logged)" value={v.offMenuReason} onChange={(e) => setV({ ...v, offMenuReason: e.target.value })} />
            </div>
          )}
          {conflict && (
            <div className="flex flex-col gap-2">
              <Alert>{conflict}</Alert>
              <Field label="Reason to save anyway (double-booking)" value={v.forceReason ?? ""} onChange={(e) => setV({ ...v, forceReason: e.target.value || null })} />
            </div>
          )}
          {save.error && !conflict && !offMenu && <Alert>{errorMessage(save.error)}</Alert>}
        </div>
      )}
    </Dialog>
  );
}

/** The visit at a glance in the pop-up: its steps once they're here, and what's in the bag. */
